// Three tiles under the sentence: how many words, how many the engine recognised, and whether the subject and the
// verb agree (the checker's first issue, in Hindi and English, with the expected form).

import type { FC, ReactNode } from 'react'
import type { VakyaParse } from '@/prakrit/components/grammar/vakya/vakya-parse'

import { CircleCheck, CircleX, Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Bi } from '@/prakrit/components/common/bi'
import { PrakritWord } from '@/prakrit/components/common/prakrit-word'

interface StatTilesProps {
    parse: VakyaParse
}

const TILE = 'flex min-w-0 flex-col gap-1 rounded-2xl border p-4'

const Count: FC<{ hi: string, en: string, value: string }> = ({ hi, en, value }): ReactNode => (
    <div className={cn(TILE, 'border-border bg-card shadow-card')}>
        <p className='text-xs font-semibold text-muted-foreground'>
            <Bi hi={hi} en={en} layout='inline' />
        </p>
        <p className='text-2xl font-semibold tabular-nums text-foreground'>{value}</p>
    </div>
)

const AgreementBody: FC<StatTilesProps> = ({ parse }): ReactNode => {
    const a = parse.agreement

    if (a.kind === 'empty' || a.kind === 'unknown') {
        return (
            <p className='flex items-start gap-2 text-sm'>
                <Info className='mt-0.5 size-4 shrink-0 text-muted-foreground' aria-hidden='true' />
                {a.kind === 'empty'
                    ? <Bi hi='पहले एक वाक्य लिखिए।' en='Type a sentence first.' />
                    : <Bi hi='कोई शब्द पहचाना नहीं गया, इसलिए मेल जाँचा नहीं जा सकता।' en='No word was recognised, so agreement cannot be checked.' />}
            </p>
        )
    }

    if (a.kind === 'cannot') {
        return (
            <p className='flex items-start gap-2 text-sm'>
                <Info className='mt-0.5 size-4 shrink-0 text-muted-foreground' aria-hidden='true' />
                <Bi hi={a.issue.hi} en={a.issue.en} hiClassName='font-semibold' />
            </p>
        )
    }

    if (a.kind === 'clash') {
        const agreementIssue = a.issue.code === 'agreement_person' || a.issue.code === 'agreement_number'

        return (
            <>
                <p className='flex items-center gap-2 text-base font-semibold text-danger'>
                    <CircleX className='size-5 shrink-0' aria-hidden='true' />
                    {agreementIssue
                        ? <Bi hi='मेल नहीं खाते' en='They do not agree' layout='inline' always />
                        : <Bi hi='यह जाँचिए' en='Check this' layout='inline' always />}
                </p>
                <Bi hi={a.issue.hi} en={a.issue.en} className='text-sm' enClassName='text-foreground/75' />
                {a.issue.expected && (
                    <p className='flex flex-wrap items-baseline gap-x-2 text-sm'>
                        <Bi hi='अपेक्षित रूप:' en='Expected form:' layout='inline' className='font-semibold' />
                        <PrakritWord word={a.issue.expected} className='pra pra-md' />
                    </p>
                )}
            </>
        )
    }

    return (
        <>
            <p className='flex items-center gap-2 text-base font-semibold text-success'>
                <CircleCheck className='size-5 shrink-0' aria-hidden='true' />
                {a.subject
                    ? <Bi hi='मेल खाते हैं' en='They agree' layout='inline' always />
                    : <Bi hi='कोई टकराव नहीं' en='Nothing clashes' layout='inline' always />}
            </p>
            <p className='text-sm'>
                {a.subject ? (
                    <Bi hi='कर्ता और क्रिया के पुरुष और वचन एक जैसे हैं।' en='The subject and the verb share person and number.' enClassName='text-foreground/75' />
                ) : (
                    <Bi
                        hi='कर्ता लिखा नहीं है; क्रिया का अन्त बताता है कि काम कौन करता है।'
                        en='No subject is written; the verb ending tells who acts.'
                        enClassName='text-foreground/75'
                    />
                )}
            </p>
        </>
    )
}

export const StatTiles: FC<StatTilesProps> = ({ parse }): ReactNode => {
    const a = parse.agreement
    const tone = a.kind === 'clash'
        ? 'border-danger/35 bg-danger-soft'
        : a.kind === 'agree'
            ? 'border-success/35 bg-success-soft'
            : 'border-border bg-surface-muted'

    return (
        <div className='grid grid-cols-2 gap-3 lg:grid-cols-[1fr_1fr_1.5fr]'>
            <Count hi='शब्द' en='Words' value={String(parse.words.length)} />
            <Count hi='पहचाने गए' en='Recognised' value={`${parse.recognised} / ${parse.words.length}`} />
            <div aria-live='polite' aria-atomic='true' className={cn(TILE, 'col-span-2 gap-1.5 lg:col-span-1', tone)}>
                <p className='text-xs font-semibold text-muted-foreground'>
                    <Bi hi='कर्ता और क्रिया का मेल' en='Subject and verb agreement' layout='inline' />
                </p>
                <AgreementBody parse={parse} />
                {parse.skipped.length > 0 && (a.kind === 'agree' || a.kind === 'clash' || a.kind === 'cannot') && (
                    <p className='text-xs text-muted-foreground'>
                        <Bi
                            hi={`अनजाने शब्द छोड़कर जाँचा गया: ${parse.skipped.join(', ')}`}
                            en={`Checked without the unknown words: ${parse.skipped.join(', ')}`}
                        />
                    </p>
                )}
            </div>
        </div>
    )
}
